import { 
    FlightChangeDetail, 
    Flight, 
    PieceFlight,
    Piece,
    ItinerarySegmentViewModel,
    ItineraryComparisonViewModel,
    PartializedLoadViewModel
} from '../types/detailInterfaces';
import { formatDateTime } from '../../../../utils/date';


const INVALID_DATE_PLACEHOLDER = '-';
const MONTH_NAMES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'] as const;

const isDevelopment = process.env.NODE_ENV === 'development';

const logWarning = (message: string): void => {
    if (isDevelopment) {
        console.warn(message);
    }
};

const logError = (message: string, error: unknown): void => {
    if (isDevelopment) {
        console.error(message, error);
    }
};

interface FormattedDateTime {
    date: string;
    time: string;
    isValid: boolean;
}

export interface ItineraryMapperOptions {
    normalizeToUtcWhenNoOffset?: boolean;
}

export class ItineraryMapper {
    private buildFormattedDateTime(isoDate: string, normalizeToUtcWhenNoOffset: boolean = true): FormattedDateTime {
        const formatted = formatDateTime(isoDate, 'es-ES', normalizeToUtcWhenNoOffset);
        return {
            date: this.formatDateForDisplay(formatted.date),
            time: formatted.time,
            isValid: formatted.isValid
        };
    }

    private formatDateForDisplay(dateString: string): string {
        if (!dateString || dateString === INVALID_DATE_PLACEHOLDER) {
            return INVALID_DATE_PLACEHOLDER;
        }

        const [day, month, year] = dateString.split('/');
        
        if (!day || !month || !year) {
            return dateString;
        }

        const monthIndex = parseInt(month, 10) - 1;
        const isValidMonthIndex = !isNaN(monthIndex) && monthIndex >= 0 && monthIndex < 12;
        
        return isValidMonthIndex 
            ? `${day} ${MONTH_NAMES[monthIndex]} ${year}`
            : dateString;
    }

    private normalizeFlown(flown: boolean | string | null | undefined): boolean | null {
        if (flown === null || flown === undefined) {
            return null;
        }
        
        if (typeof flown === 'boolean') {
            return flown;
        }
        
        const stringToBoolean: Record<string, boolean> = {
            'true': true,
            'false': false
        };
        
        return stringToBoolean[flown] ?? null;
    }

    private mapFlightToSegment(flight: Flight, normalizeToUtcWhenNoOffset: boolean = true): ItinerarySegmentViewModel {
        const flown = this.normalizeFlown(flight.flown);
        
        return {
            flightNumber: flight.flightNumber,
            originAirport: flight.fromAirport,
            destinationAirport: flight.toAirport,
            etd: this.buildFormattedDateTime(flight.etd, normalizeToUtcWhenNoOffset),
            eta: this.buildFormattedDateTime(flight.eta, normalizeToUtcWhenNoOffset),
            leg: flight.leg,
            flown
        };
    }

    private mapFlightsToSegments(flights: Flight[], normalizeToUtcWhenNoOffset: boolean = true): ItinerarySegmentViewModel[] {
        if (!flights?.length) {
            return [];
        }

        const sortedFlights = [...flights].sort((a, b) => a.leg - b.leg);
        
        return sortedFlights.map(flight => this.mapFlightToSegment(flight, normalizeToUtcWhenNoOffset));
    }

    private mapPieceFlightToSegment(pieceFlight: PieceFlight, normalizeToUtcWhenNoOffset: boolean = true): ItinerarySegmentViewModel {
        if (!pieceFlight?.flightNo || pieceFlight.leg === undefined) {
            throw new Error('Invalid PieceFlight data: missing flightNo or leg');
        }

        const flown = this.normalizeFlown(pieceFlight.flown);

        return {
            flightNumber: pieceFlight.flightNo,
            originAirport: pieceFlight.from || '',
            destinationAirport: pieceFlight.to || '',
            etd: this.buildFormattedDateTime(pieceFlight.etd, normalizeToUtcWhenNoOffset),
            eta: this.buildFormattedDateTime(pieceFlight.eta, normalizeToUtcWhenNoOffset),
            leg: pieceFlight.leg,
            flown
        };
    }

    private mapPieceToPartializedLoad(piece: Piece, index: number, normalizeToUtcWhenNoOffset: boolean = true): PartializedLoadViewModel | null {
        if (!piece.flights?.length) {
            logWarning(`Piece ${index + 1} has no flights`);
            return null;
        }

        const sortedFlights = [...piece.flights].sort((a, b) => a.leg - b.leg);
        
        const segments = sortedFlights
            .map(pieceFlight => this.safeMapPieceFlightToSegment(pieceFlight, normalizeToUtcWhenNoOffset))
            .filter((segment): segment is ItinerarySegmentViewModel => segment !== null);

        return {
            partNumber: index + 1,
            piecesCount: this.parsePiecesCount(piece.piecesTotal),
            segments
        };
    }

    private safeMapPieceFlightToSegment(pieceFlight: PieceFlight, normalizeToUtcWhenNoOffset: boolean = true): ItinerarySegmentViewModel | null {
        try {
            return this.mapPieceFlightToSegment(pieceFlight, normalizeToUtcWhenNoOffset);
        } catch (error) {
            logError('Error mapping piece flight:', error);
            return null;
        }
    }

    private mapPiecesToPartializedLoads(pieces: Piece[], totalPieces?: number, normalizeToUtcWhenNoOffset: boolean = true): PartializedLoadViewModel[] {
        if (!pieces?.length) {
            return [];
        }

        const mappedLoads = pieces
            .map((piece, index) => this.mapPieceToPartializedLoad(piece, index, normalizeToUtcWhenNoOffset))
            .filter((load): load is PartializedLoadViewModel => load !== null);

        this.validateTotalPieces(mappedLoads, totalPieces);

        return mappedLoads;
    }

    private validateTotalPieces(mappedLoads: PartializedLoadViewModel[], totalPieces?: number): void {
        if (totalPieces === undefined) {
            return;
        }

        const totalMappedPieces = mappedLoads.reduce((sum, load) => sum + load.piecesCount, 0);
        
        if (totalMappedPieces !== totalPieces) {
            logWarning(`Pieces count mismatch: expected ${totalPieces}, got ${totalMappedPieces}`);
        }
    }

    private parsePiecesCount(piecesTotal: string): number {
        const parsed = parseInt(piecesTotal, 10);
        
        if (isNaN(parsed) || parsed < 0) {
            logWarning(`Invalid piecesTotal: "${piecesTotal}", defaulting to 0`);
            return 0;
        }
        
        return parsed;
    }

    /**
     * A shipment is considered partialized when the cargo is split across
     * multiple piece groups (not just multiple flights within the same group)
     */
    private isPartialized(pieces: Piece[] | null): boolean {
        return pieces !== null && pieces.length > 1;
    }

    private getValidFlights(flights: Flight[]): Flight[] {
        return flights.filter(flight => flight?.flightNumber && flight?.leg !== undefined);
    }

    mapToComparisonViewModel(data: FlightChangeDetail, options?: ItineraryMapperOptions): ItineraryComparisonViewModel {
        if (!data?.itineraryChange) {
            return { previous: [], new: [] };
        }

        const normalizeToUtcWhenNoOffset = options?.normalizeToUtcWhenNoOffset !== false;

        const { originalItinerary, newItinerary } = data.itineraryChange;
        
        const originalFlights = this.getValidFlights(originalItinerary?.flights || []);
        const newFlights = this.getValidFlights(newItinerary?.flights || []);
        const originalPieces = originalItinerary?.pieces || null;
        const newPieces = newItinerary?.pieces || null;

        const isOriginalPartialized = this.isPartialized(originalPieces);
        const isNewPartialized = this.isPartialized(newPieces);
        const isCompleteLoad = isOriginalPartialized && !isNewPartialized;
        
        const hasFlownFlights = originalFlights.some(flight => flight.flown === true);
        const isPartializedInTransit = hasFlownFlights && isNewPartialized;

        return {
            previous: isOriginalPartialized ? [] : this.mapFlightsToSegments(originalFlights, normalizeToUtcWhenNoOffset),
            new: isNewPartialized ? [] : this.mapFlightsToSegments(newFlights, normalizeToUtcWhenNoOffset),
            isOriginalPartialized,
            ...(isOriginalPartialized && originalPieces && {
                originalPartializedLoads: this.mapPiecesToPartializedLoads(originalPieces, data.totalPieces, normalizeToUtcWhenNoOffset)
            }),
            isPartialized: isNewPartialized,
            ...(isNewPartialized && newPieces && {
                partializedLoads: this.mapPiecesToPartializedLoads(newPieces, data.totalPieces, normalizeToUtcWhenNoOffset)
            }),
            ...(isCompleteLoad && { isCompleteLoad: true }),
            ...(isPartializedInTransit && { isPartializedInTransit: true })
        };
    }
}

export const itineraryMapper = new ItineraryMapper();

export const mapToItineraryComparison = (
    data: FlightChangeDetail,
    options?: ItineraryMapperOptions
): ItineraryComparisonViewModel => {
    return itineraryMapper.mapToComparisonViewModel(data, options);
};
